/**
 * Insight engine — deterministic AI-style observations derived from the
 * extracted metrics and classified nodes. No LLM call: every insight is a
 * verifiable computation over the data already on screen.
 */

import { formatMoney } from "./format";
import type { TSemanticNode, TSummaryInsight, TSummaryMetric } from "./types";

function findMetric(metrics: TSummaryMetric[], concept: string, labelPattern: RegExp): TSummaryMetric | undefined {
  return metrics.find((m) => m.concept === concept && labelPattern.test(m.label));
}

function findTagList(nodes: TSemanticNode[], concept: string): string[] | null {
  for (const node of nodes) {
    if (node.concept === concept && node.dataType === "tagList" && Array.isArray(node.value)) {
      return (node.value as unknown[]).map(String);
    }
    if (node.children) {
      const nested = findTagList(node.children, concept);
      if (nested) return nested;
    }
  }
  return null;
}

export function deriveInsights(nodes: TSemanticNode[], metrics: TSummaryMetric[]): TSummaryInsight[] {
  const insights: TSummaryInsight[] = [];

  const mrr = findMetric(metrics, "revenue", /mrr/i);
  const burn = findMetric(metrics, "financial", /burn/i);
  const raise = findMetric(metrics, "fundraising", /raise|round/i);

  if (mrr?.numeric && burn?.numeric && mrr.numeric > 0) {
    const multiple = burn.numeric / mrr.numeric;
    if (multiple <= 2) {
      insights.push({ id: "capital-efficiency", tone: "positive", text: `Capital efficient — burn is only ${multiple.toFixed(1)}× monthly revenue.` });
    } else if (multiple >= 4) {
      insights.push({ id: "capital-efficiency", tone: "caution", text: `Burn runs ${multiple.toFixed(1)}× ahead of revenue (${formatMoney(burn.numeric)} vs ${formatMoney(mrr.numeric)} MRR) — typical for this stage, but worth a plan to close the gap.` });
    } else {
      insights.push({ id: "capital-efficiency", tone: "neutral", text: `Burn is ${multiple.toFixed(1)}× monthly revenue — investing ahead of revenue at a controlled pace.` });
    }
  }

  if (raise?.numeric && burn?.numeric && burn.numeric > 0) {
    const months = Math.round(raise.numeric / burn.numeric);
    insights.push({
      id: "runway",
      tone: months >= 18 ? "positive" : months >= 12 ? "neutral" : "caution",
      text: `The ${formatMoney(raise.numeric)} raise covers ~${months} months at current burn.`,
    });
  }

  const industries = findTagList(nodes, "industry");
  if (industries) {
    if (industries.length >= 8) {
      insights.push({ id: "sector-breadth", tone: "neutral", text: `Broad sector coverage (${industries.length} industries) — reads as a generalist profile.` });
    } else if (industries.length <= 2) {
      insights.push({ id: "sector-breadth", tone: "neutral", text: `Tightly focused on ${industries.join(" and ")}.` });
    }
  }

  const geos = findTagList(nodes, "geography");
  if (geos) {
    if (geos.length >= 4) {
      insights.push({ id: "geo-breadth", tone: "positive", text: `Wide geographic mandate spanning ${geos.length} regions.` });
    } else if (geos.length === 1) {
      insights.push({ id: "geo-breadth", tone: "caution", text: `Geographic exposure concentrated in ${geos[0]}.` });
    }
  }

  const checkSize = metrics.find((m) => m.concept === "checkSize");
  if (checkSize?.display.includes("–")) {
    insights.push({ id: "check-flexibility", tone: "positive", text: `Flexible check size (${checkSize.display}) — can participate across round sizes.` });
  }

  const stages = findTagList(nodes, "stage");
  if (stages && stages.length >= 3) {
    insights.push({ id: "stage-breadth", tone: "neutral", text: `Invests across ${stages.length} stages, from earliest to growth.` });
  }

  return insights.slice(0, 4);
}
